import { useCallback, useEffect, useRef, useState } from 'react'

import { isApiError } from '../api/errors'
import {
  fetchLibraryCatalogState,
  type LibraryCatalogState,
} from '../api/referenceImages'
import { isAbortError } from '../lib/isAbortError'

export type { LibraryCatalogState }

type UseLibraryCatalogOptions = {
  /** 为 false 时不请求图库（弹窗/参考图选择等场景再置 true） */
  enabled?: boolean
}

/**
 * 生图页与存入图库弹窗共用：全量图库记录 + 已关联时间线条目的 genItemId 集合。
 */
export function useLibraryCatalog(options: UseLibraryCatalogOptions = {}) {
  const { enabled = false } = options
  const [catalog, setCatalog] = useState<LibraryCatalogState | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const catalogFetchAbortRef = useRef<AbortController | null>(null)

  const refresh = useCallback(async () => {
    catalogFetchAbortRef.current?.abort()
    const ac = new AbortController()
    catalogFetchAbortRef.current = ac
    const signal = ac.signal
    setLoading(true)
    setError(null)
    try {
      const next = await fetchLibraryCatalogState(signal)
      if (signal.aborted) return
      setCatalog(next)
    } catch (e) {
      if (signal.aborted || isAbortError(e)) return
      setError(isApiError(e) ? e.message : '图库加载失败')
      setCatalog({ items: [], genItemIds: new Set(), folderPaths: [] })
    } finally {
      if (!signal.aborted) setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!enabled) return
    void refresh()
  }, [enabled, refresh])

  useEffect(() => {
    return () => {
      catalogFetchAbortRef.current?.abort()
      catalogFetchAbortRef.current = null
    }
  }, [])

  return { catalog, loading, error, refresh }
}
